/**
 * Filter Constants for Global Filter Strip (GO-35)
 */

import type {
  DateRangePreset,
  ConductDimension,
  RegulatoryFlag,
  FilterLogic,
} from '../types/filter.types';

// =============================================================================
// Date Range Presets
// =============================================================================

export const DATE_RANGE_PRESETS: Array<{ value: DateRangePreset; label: string }> = [
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'custom', label: 'Custom range' },
];

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  last_quarter: 'Last quarter',
  custom: 'Custom range',
};

// =============================================================================
// Conduct Dimensions
// =============================================================================

export const DIMENSION_OPTIONS: Array<{ value: ConductDimension; label: string; description: string }> = [
  {
    value: 'dissatisfaction',
    label: 'Dissatisfaction',
    description: 'Customer expressed dissatisfaction or complaint',
  },
  {
    value: 'strain',
    label: 'Strain',
    description: 'Customer experiencing financial or emotional strain',
  },
  {
    value: 'harm',
    label: 'Harm',
    description: 'Potential foreseeable harm detected',
  },
  {
    value: 'tcf',
    label: 'TCF',
    description: 'Treating Customers Fairly outcome labels imported from Analyse',
  },
];

export const DIMENSION_LABELS: Record<ConductDimension, string> = {
  dissatisfaction: 'Dissatisfaction',
  strain: 'Strain',
  harm: 'Harm',
  tcf: 'TCF',
};

// =============================================================================
// Regulatory Flags
// =============================================================================

export const REGULATORY_FLAG_OPTIONS: Array<{ value: RegulatoryFlag; label: string; description: string }> = [
  {
    value: 'vulnerability',
    label: 'Vulnerability',
    description: 'Customer vulnerability indicators detected',
  },
  {
    value: 'complaint',
    label: 'Complaint',
    description: 'Eligible complaint detected',
  },
];

export const REGULATORY_FLAG_LABELS: Record<RegulatoryFlag, string> = {
  vulnerability: 'Vulnerability',
  complaint: 'Complaint',
};

// =============================================================================
// URL Parameter Keys
// =============================================================================

export const FILTER_PARAM_KEYS = [
  'dateRangePreset',
  'dateFrom',
  'dateTo',
  // Sentinel for an explicitly-cleared date range ("all time / no filter"),
  // distinct from "no date filter chosen yet". Lets the cleared state survive a
  // URL round-trip / deep link even when other filter params are present.
  'dateRangeCleared',
  'productCategories',
  'productCategoriesLogic',
  'channels',
  'channelsLogic',
  'journeyStages',
  'journeyStagesLogic',
  'dimensions',
  'dimensionsLogic',
  'regulatoryFlags',
  'regulatoryFlagsLogic',
  'search',
  'conductScoreMin',
  'conductScoreMax',
] as const;

// =============================================================================
// Valid Values for URL Param Validation
// =============================================================================

/** Valid date range preset values for URL param validation */
export const VALID_DATE_RANGE_PRESETS = ['last_7_days', 'last_30_days', 'last_quarter', 'custom'] as const;

/** Valid dimension values for URL param validation */
export const VALID_DIMENSIONS = ['dissatisfaction', 'strain', 'harm', 'tcf'] as const;

/** Valid regulatory flag values for URL param validation */
export const VALID_REGULATORY_FLAGS = ['vulnerability', 'complaint'] as const;

/** Valid filter logic values for URL param validation */
export const VALID_FILTER_LOGIC = ['and', 'or'] as const;

/** Default logic modes for each filter type */
export const DEFAULT_FILTER_LOGIC: Record<string, FilterLogic> = {
  dimensions: 'or',
  regulatoryFlags: 'and',
  productCategories: 'or',
  channels: 'or',
  journeyStages: 'or',
};

// =============================================================================
// Storage Keys
// =============================================================================

/** localStorage key for persisting filter state */
export const FILTER_STORAGE_KEY = 'go_collection_filters';

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format date range filter value for display.
 * Used by DateRangeFilter and ActiveFilterChips.
 */
export function formatDateRangeLabel(
  preset?: DateRangePreset,
  from?: string,
  to?: string
): string {
  if (preset && preset !== 'custom') {
    return DATE_RANGE_PRESET_LABELS[preset];
  }
  if (from && to) {
    const fromDate = new Date(from).toLocaleDateString();
    const toDate = new Date(to).toLocaleDateString();
    return `${fromDate} - ${toDate}`;
  }
  if (from) {
    return `From ${new Date(from).toLocaleDateString()}`;
  }
  if (to) {
    return `Until ${new Date(to).toLocaleDateString()}`;
  }
  return 'Custom range';
}
